import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import {
  ArrowDownUp,
  ArrowLeft,
  ArrowRight,
  Check,
  Copy,
  KeyRound,
  Lock,
  Phone,
  RefreshCw,
  Scale,
  Scan,
  Server,
  ShieldCheck,
  Smartphone,
  Timer,
  Wifi,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  Button,
  Callout,
  Checkbox,
  Dialog,
  FormField,
  Icon,
  Input,
  Select,
} from "@obsina/design-system";
import {
  COMMON_DIAL_CODES,
  cleanPhoneNumber,
  composePhoneNumber,
  DEFAULT_DIAL_CODE,
  formatCountdown,
  formatPairingCode,
  formatPhonePreview,
  isPairingCode,
  isRenderableQrDataUrl,
  isValidPhoneNumber,
  normalizeNationalNumber,
} from "../whatsapp-qr";
import { usePairing } from "./usePairing";
import type { WhatsAppWizardApi } from "./api";
import { WHATSAPP_CONSENT_TEXT_VERSION, whatsappMessages } from "./messages";
import type { WhatsAppMessages } from "./messages";
import "./whatsapp-wizard.css";

/** Sentinel dial-code option that reveals a free-entry country-code field. */
const OTHER_COUNTRY = "other";
const DIALOG_FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "summary",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function trapDialogFocus(event: ReactKeyboardEvent<HTMLDialogElement>) {
  if (event.key !== "Tab") return;
  const dialog = event.currentTarget;
  const controls = [...dialog.querySelectorAll<HTMLElement>(DIALOG_FOCUSABLE)];
  const first = controls[0];
  const last = controls.at(-1);
  if (!first || !last) {
    event.preventDefault();
    dialog.focus();
    return;
  }
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === dialog)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

export interface WhatsAppWizardProps {
  readonly api: WhatsAppWizardApi;
  /** Controlled modal state. Defaults to open for focused component tests. */
  readonly open?: boolean;
  readonly messages?: WhatsAppMessages;
  readonly onCancel?: () => void;
  readonly onFinish?: (connectionId: string) => void;
  /** Poll interval override for tests. */
  readonly pollIntervalMs?: number;
}

type WizardStep = 1 | 2;

export function WhatsAppWizard({
  api,
  open = true,
  messages = whatsappMessages,
  onCancel,
  onFinish,
  pollIntervalMs,
}: WhatsAppWizardProps) {
  const [step, setStep] = useState<WizardStep>(1);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  const consentRequest = useRef<AbortController | null>(null);
  const session = useRef(0);

  const reset = useCallback(() => {
    session.current += 1;
    consentRequest.current?.abort();
    consentRequest.current = null;
    setStep(1);
    setConnectionId(null);
    setChecked(false);
    setSubmitting(false);
    setFailed(false);
  }, []);

  const dismiss = () => {
    reset();
    onCancel?.();
  };

  const submitConsent = () => {
    if (!checked || submitting) return;
    const requestSession = session.current;
    const controller = new AbortController();
    consentRequest.current?.abort();
    consentRequest.current = controller;
    setSubmitting(true);
    setFailed(false);
    void api
      .recordConsent(controller.signal)
      .then((result) => {
        if (requestSession !== session.current || controller.signal.aborted) {
          return;
        }
        consentRequest.current = null;
        setConnectionId(result.connectionId);
        setSubmitting(false);
        setStep(2);
      })
      .catch(() => {
        if (requestSession !== session.current || controller.signal.aborted) {
          return;
        }
        consentRequest.current = null;
        setFailed(true);
        setSubmitting(false);
      });
  };

  const finish = (id: string) => {
    reset();
    onFinish?.(id);
  };

  useEffect(
    () => () => {
      session.current += 1;
      consentRequest.current?.abort();
    },
    [],
  );

  // A controlling parent may close the modal independently (route changes or
  // feature teardown). Treat that exactly like the explicit close paths so a
  // later open never revives consent, errors, or a stale pairing identifier.
  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  // The native modal makes the application inert, while this lock prevents the
  // document behind it from moving under trackpad or touch scrolling. Restore
  // the exact prior inline value so this flow does not own global page policy.
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = previousOverflow;
    };
  }, [open]);

  const footer =
    step === 1 ? (
      <div className="wa-dialog-actions">
        <Button
          iconStart={<Icon icon={X} size={16} />}
          onClick={dismiss}
          variant="secondary"
        >
          {messages.consent.cancel}
        </Button>
        <div className="wa-actions">
          <span className="wa-foot-hint">
            <Icon icon={Lock} size={14} />
            {messages.consent.footHint}
          </span>
          <Button
            disabled={!checked || submitting}
            iconEnd={<Icon icon={ArrowRight} size={16} />}
            loading={submitting}
            loadingLabel={messages.consent.submitting}
            onClick={submitConsent}
          >
            {messages.consent.continue}
          </Button>
        </div>
      </div>
    ) : (
      <div className="wa-dialog-actions">
        <Button
          iconStart={<Icon icon={ArrowLeft} size={16} />}
          onClick={() => setStep(1)}
          variant="secondary"
        >
          {messages.pairing.back}
        </Button>
        <span className="wa-foot-hint">
          <Icon icon={Wifi} size={14} />
          {messages.pairing.waitingStatus}
        </span>
      </div>
    );

  return (
    <Dialog
      autoFocus
      className="wa-connect-dialog"
      closeOnBackdrop={false}
      description={messages.dialog.description}
      footer={footer}
      onKeyDown={trapDialogFocus}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) dismiss();
      }}
      open={open}
      size="lg"
      tabIndex={-1}
      title={messages.dialog.title}
    >
      <div className="wa-wizard" data-step={step}>
        <WizardStepper current={step} messages={messages} />
        {step === 1 && (
          <ConsentStep
            checked={checked}
            failed={failed}
            messages={messages}
            onCheckedChange={setChecked}
          />
        )}
        {step === 2 && connectionId && (
          <PairingStep
            api={api}
            connectionId={connectionId}
            messages={messages}
            onConnected={() => finish(connectionId)}
            pollIntervalMs={pollIntervalMs}
          />
        )}
      </div>
    </Dialog>
  );
}

function WizardStepper({
  current,
  messages,
}: {
  current: WizardStep;
  messages: WhatsAppMessages;
}) {
  const names = [messages.stepper.consent, messages.stepper.linkDevice];
  return (
    <ol aria-label={messages.stepper.label} className="wa-stepper">
      {names.map((name, position) => {
        const index = position + 1;
        const state =
          index < current ? "done" : index === current ? "current" : "upcoming";
        return (
          <Fragment key={name}>
            {position > 0 && (
              <li
                aria-hidden="true"
                className="wa-step-line"
                data-state={index <= current ? "done" : "upcoming"}
              />
            )}
            <li
              aria-current={state === "current" ? "step" : undefined}
              aria-label={`${messages.stepper.stepPrefix(index)}: ${name}${
                state === "done" ? " ✓" : ""
              }`}
              className="wa-step"
              data-state={state}
            >
              <span aria-hidden="true" className="wa-step__dot">
                {state === "done" ? <Icon icon={Check} size={17} /> : index}
              </span>
              <span aria-hidden="true" className="wa-step__text">
                <span className="wa-step__index">
                  {messages.stepper.stepPrefix(index)}
                </span>
                <span className="wa-step__name">{name}</span>
              </span>
            </li>
          </Fragment>
        );
      })}
    </ol>
  );
}

const CONSENT_POINT_ICONS: readonly LucideIcon[] = [
  ArrowDownUp,
  ShieldCheck,
  Server,
];

function ConsentStep({
  checked,
  failed,
  messages,
  onCheckedChange,
}: {
  checked: boolean;
  failed: boolean;
  messages: WhatsAppMessages;
  onCheckedChange: (checked: boolean) => void;
}) {
  const detailId = useId();

  return (
    <div className="wa-panel">
      <div className="wa-panel__body">
        <p className="wa-eyebrow">{messages.consent.eyebrow}</p>
        <h3 className="wa-title">{messages.consent.title}</h3>
        <p className="wa-lede">{messages.consent.lede}</p>
        <ul className="wa-points">
          {messages.consent.points.map((point, index) => (
            <li className="wa-point" key={point.title}>
              <span className="wa-point__icon">
                <Icon
                  icon={CONSENT_POINT_ICONS[index] ?? ShieldCheck}
                  size={17}
                />
              </span>
              <span className="wa-point__copy">
                <span className="wa-point__title">{point.title}</span>
                <span className="wa-point__body">{point.body}</span>
              </span>
            </li>
          ))}
        </ul>
        <Callout icon={Scale} tone="warning">
          {messages.consent.kvkkCallout}
        </Callout>
        <details className="wa-notice">
          <summary>{messages.consent.noticeToggle}</summary>
          <p className="wa-notice__title">{messages.consent.noticeTitle}</p>
          {messages.consent.noticeSections.map((section) => (
            <div className="wa-notice__section" key={section.heading}>
              <h4>{section.heading}</h4>
              <p>{section.body}</p>
            </div>
          ))}
        </details>
        <div className="wa-consent-box" data-checked={checked}>
          <Checkbox
            checked={checked}
            label={messages.consent.checkboxLabel}
            onChange={(event) => onCheckedChange(event.currentTarget.checked)}
            aria-describedby={detailId}
          />
          <p className="wa-consent-detail" id={detailId}>
            {messages.consent.checkboxDetail}
          </p>
        </div>
        <p className="wa-version">
          {messages.consent.versionLabel(WHATSAPP_CONSENT_TEXT_VERSION)}
        </p>
        {failed && (
          <Callout announce tone="danger">
            {messages.consent.error}
          </Callout>
        )}
      </div>
    </div>
  );
}

type PairingHook = ReturnType<typeof usePairing>;

function PairingStep({
  api,
  connectionId,
  messages,
  onConnected,
  pollIntervalMs,
}: {
  api: WhatsAppWizardApi;
  connectionId: string;
  messages: WhatsAppMessages;
  onConnected: () => void;
  pollIntervalMs?: number;
}) {
  const pairing = usePairing(api, connectionId, onConnected, pollIntervalMs);
  const [mode, setMode] = useState<"qr" | "code">("qr");
  const instructions =
    mode === "code"
      ? messages.pairing.code.instructions
      : messages.pairing.instructions;

  return (
    <div className="wa-panel">
      <div className="wa-panel__body">
        <div className="wa-pair">
          <div>
            <p className="wa-eyebrow">
              {mode === "code"
                ? messages.pairing.code.eyebrow
                : messages.pairing.eyebrow}
            </p>
            <h3 className="wa-title">
              {mode === "code"
                ? messages.pairing.code.title
                : messages.pairing.title}
            </h3>
            <p className="wa-lede">
              {mode === "code"
                ? messages.pairing.code.lede
                : messages.pairing.lede}
            </p>
            <p className="wa-instr-title">
              {mode === "code"
                ? messages.pairing.code.instructionsTitle
                : messages.pairing.instructionsTitle}
            </p>
            <ol className="wa-instr">
              {instructions.map((line, index) => (
                <li key={line}>
                  <span className="wa-instr__num">{index + 1}</span>
                  <span>{line}</span>
                </li>
              ))}
            </ol>
            <button
              className="wa-mode-toggle"
              onClick={() => setMode(mode === "qr" ? "code" : "qr")}
              type="button"
            >
              <Icon icon={mode === "qr" ? Smartphone : Scan} size={15} />
              {mode === "qr"
                ? messages.pairing.code.toggle
                : messages.pairing.code.qrToggle}
            </button>
          </div>
          <div className="wa-qr-wrap">
            {pairing.phase === "error" ? (
              <Callout announce tone="danger">
                {messages.pairing.error}
              </Callout>
            ) : pairing.phase === "logged_out" ? (
              <Callout
                announce
                title={messages.pairing.loggedOut}
                tone="warning"
              />
            ) : mode === "code" ? (
              // Code mode owns the linking ("scanning") state too, so a live
              // code is never hidden behind the shared spinner.
              <CodePanel messages={messages} pairing={pairing} />
            ) : pairing.phase === "scanning" ? (
              <>
                <span aria-hidden="true" className="wa-spin" />
                <p className="wa-scan-note">{messages.pairing.scanning}</p>
                <p className="obsina-sr-only" role="status">
                  {messages.pairing.scanningStatus}
                </p>
              </>
            ) : (
              <QrPanel messages={messages} pairing={pairing} />
            )}
            {pairing.phase === "error" && (
              <div className="wa-qr-actions">
                <Button
                  iconStart={<Icon icon={RefreshCw} size={16} />}
                  onClick={() => pairing.refresh()}
                  variant="ghost"
                >
                  {messages.pairing.retry}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function QrPanel({
  messages,
  pairing,
}: {
  messages: WhatsAppMessages;
  pairing: PairingHook;
}) {
  const showQr =
    pairing.phase === "ready" && isRenderableQrDataUrl(pairing.view?.qrDataUrl);
  return (
    <>
      {pairing.phase === "expired" ? (
        <Callout announce title={messages.pairing.expired} tone="warning" />
      ) : showQr ? (
        <>
          <div className="wa-qr-frame">
            <img alt={messages.pairing.qrAlt} src={pairing.view?.qrDataUrl} />
          </div>
          {pairing.secondsLeft !== null && (
            <span className="wa-qr-timer" data-warn={pairing.secondsLeft <= 10}>
              <Icon icon={Timer} size={14} />
              {messages.pairing.countdown(formatCountdown(pairing.secondsLeft))}
            </span>
          )}
          <p className="wa-qr-note">{messages.pairing.waiting}</p>
          <p className="obsina-sr-only" role="status">
            {messages.pairing.waitingStatus}
          </p>
        </>
      ) : (
        <>
          <span aria-hidden="true" className="wa-spin" />
          <p className="wa-scan-note">{messages.pairing.preparing}</p>
        </>
      )}
      <div className="wa-qr-actions">
        <Button
          iconStart={<Icon icon={RefreshCw} size={16} />}
          loading={pairing.refreshing}
          loadingLabel={messages.pairing.refreshing}
          onClick={() => pairing.refresh()}
          variant="ghost"
        >
          {messages.pairing.newCode}
        </Button>
      </div>
    </>
  );
}

function CodePanel({
  messages,
  pairing,
}: {
  messages: WhatsAppMessages;
  pairing: PairingHook;
}) {
  const copy = messages.pairing.code;
  const [country, setCountry] = useState<string>(DEFAULT_DIAL_CODE);
  const [customCode, setCustomCode] = useState("");
  const [national, setNational] = useState("");
  const [copied, setCopied] = useState(false);
  const effectiveCc =
    country === OTHER_COUNTRY ? cleanPhoneNumber(customCode) : country;
  const nationalDigits = normalizeNationalNumber(effectiveCc, national);
  const composed = composePhoneNumber(effectiveCc, national);
  const phoneValid = isValidPhoneNumber(composed);
  const hasEntry = nationalDigits.length > 0;
  const code = pairing.view?.pairingCode;

  const submit = () => {
    if (phoneValid) pairing.startCode(composed);
  };

  const copyCode = () => {
    if (!isPairingCode(code) || !navigator.clipboard) return;
    void navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopied(true);
        globalThis.setTimeout(() => setCopied(false), 2_000);
      })
      .catch(() => undefined);
  };

  // A live code always wins, even while the bridge reports the "pairing"
  // (linking) state, so an issued code is never hidden behind a spinner.
  if (isPairingCode(code)) {
    return (
      <div className="wa-code-wrap">
        <p className="wa-code-heading">{copy.codeHeading}</p>
        <div className="wa-code-row">
          <span className="wa-code" translate="no">
            {formatPairingCode(code)}
          </span>
          <Button
            iconStart={<Icon icon={copied ? Check : Copy} size={15} />}
            onClick={copyCode}
            size="sm"
            variant="secondary"
          >
            {copied ? copy.copied : copy.copy}
          </Button>
        </div>
        {pairing.secondsLeft !== null && (
          <span className="wa-qr-timer" data-warn={pairing.secondsLeft <= 10}>
            <Icon icon={Timer} size={14} />
            {messages.pairing.countdown(formatCountdown(pairing.secondsLeft))}
          </span>
        )}
        <p className="wa-qr-note">{copy.waiting}</p>
        <p className="obsina-sr-only" role="status">
          {copy.waitingStatus(code)}
        </p>
        <div className="wa-qr-actions">
          <Button
            iconStart={<Icon icon={RefreshCw} size={16} />}
            loading={pairing.codeSubmitting}
            loadingLabel={copy.gettingCode}
            onClick={() => pairing.startCode(composed)}
            variant="ghost"
          >
            {copy.newCode}
          </Button>
        </div>
      </div>
    );
  }

  // The bridge dropped the code and reports linking: show the shared spinner
  // rather than falling back to the phone form under the user's feet.
  if (pairing.phase === "scanning") {
    return (
      <>
        <span aria-hidden="true" className="wa-spin" />
        <p className="wa-scan-note">{messages.pairing.scanning}</p>
        <p className="obsina-sr-only" role="status">
          {messages.pairing.scanningStatus}
        </p>
      </>
    );
  }

  return (
    <form
      className="wa-phone-form"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <Select
        label={copy.countryLabel}
        onChange={(event) => setCountry(event.currentTarget.value)}
        options={[
          ...COMMON_DIAL_CODES.map((dial) => ({
            value: dial.code,
            label: `${dial.name} (+${dial.code})`,
          })),
          { value: OTHER_COUNTRY, label: copy.otherCountry },
        ]}
        value={country}
      />
      {country === OTHER_COUNTRY && (
        <FormField label={copy.customCodeLabel}>
          <Input
            inputMode="numeric"
            onChange={(event) => setCustomCode(event.currentTarget.value)}
            placeholder={copy.customCodePlaceholder}
            value={customCode}
          />
        </FormField>
      )}
      <FormField
        error={hasEntry && !phoneValid ? copy.phoneInvalid : undefined}
        hint={copy.phoneHint}
        label={copy.phoneLabel}
      >
        <Input
          autoComplete="tel-national"
          inputMode="tel"
          onChange={(event) => setNational(event.currentTarget.value)}
          placeholder={copy.phonePlaceholder}
          type="tel"
          value={national}
        />
      </FormField>
      {hasEntry && (
        <p className="wa-phone-preview" data-invalid={!phoneValid}>
          <Icon icon={Phone} size={14} />
          <span>
            {copy.preview(formatPhonePreview(effectiveCc, nationalDigits))}
          </span>
        </p>
      )}
      <Button
        disabled={!phoneValid}
        iconStart={<Icon icon={KeyRound} size={16} />}
        loading={pairing.codeSubmitting}
        loadingLabel={copy.gettingCode}
        type="submit"
      >
        {copy.getCode}
      </Button>
    </form>
  );
}
